import { Box, Divider, Typography } from '@mui/material';
import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	loadOrderVatCorrectionsForOrderRequest,
	clearOrderVatCorrectionsForOrder,
} from 'redux/reducers/emVatCorrectionsImport/reducer';
import {
	selectOrderVatCorrectionsLoading,
	selectOrderVatCorrectionsRows,
} from 'redux/reducers/emVatCorrectionsImport/selectors';
import { EasCard } from 'ui/EasCard';
import { TOrderVatCorrectionsProps } from './types';
import { formatCorrectionDateTime, getReportingPeriodLabel, getSchemeLabel } from './utils';
import { EasTableValue } from '../../../../ui/EasTable/EasTableRow/EasTableValue';
import { ETableStructureType } from '../../../../ui/EasTable/types';
import { tableStructure } from 'components/EmVatCorrections/tableStructure';

export const OrderVatCorrections: FC<TOrderVatCorrectionsProps> = ({ orderId }) => {
	const dispatch = useDispatch();
	const rows = useSelector(selectOrderVatCorrectionsRows);
	const loading = useSelector(selectOrderVatCorrectionsLoading);

	useEffect(() => {
		dispatch(loadOrderVatCorrectionsForOrderRequest({ orderId }));
		return () => {
			dispatch(clearOrderVatCorrectionsForOrder());
		};
	}, [dispatch, orderId]);

	return (
		<EasCard
			id="order-vat-corrections-card"
			title="VAT Corrections"
			action={<></>}
			loading={loading}
		>
			<Box
				sx={{
					border: '1px solid',
					borderColor: 'divider',
					borderRadius: '12px',
					p: 1.5,
					mt: 0.5,
					minHeight: loading ? 48 : undefined,
				}}
			>
				{!loading && rows.length === 0 && (
					<Typography variant="body2" color="text.secondary">
						No VAT corrections found for this order.
					</Typography>
				)}
				{rows.map((row, index) => {
					const createdAt = formatCorrectionDateTime(row.requested_on);
					const approvalAt = formatCorrectionDateTime(row.approved_rejected_on);
					const remarks = row.action_remarks?.trim();

					return (
						<Box key={row.id}>
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									alignItems: 'center',
									gap: 1,
									rowGap: 0.5,
								}}
							>
								{createdAt && (
									<Typography variant="body2" color="text.secondary">
										Created at: {createdAt}
									</Typography>
								)}
								{row.approval_status && (
									<EasTableValue
										value={row.approval_status_for_filter}
										type={ETableStructureType.STATUS}
										size="m"
										item={row}
										tableStructureItem={tableStructure.find(
											(s) => s.selector === 'approval_status_for_filter',
										)}
									/>
								)}
								{approvalAt && (
									<Typography variant="body2" color="text.secondary">
										Approval date: {approvalAt}
									</Typography>
								)}
							</Box>
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: 1,
									mt: 1.5,
								}}
							>
								<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
									<Typography variant="subtitle1" color="primary" fontWeight="bold">
										{getSchemeLabel(row.scheme)}
									</Typography>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
										<Typography variant="body2" color="text.secondary">
											Reporting period:
										</Typography>
										<EasTableValue
											value={getReportingPeriodLabel(row)}
											type={ETableStructureType.STATUS}
											size="m"
											item={row}
											tableStructureItem={tableStructure.find(
												(s) => s.selector === 'correction_peeriod',
											)}
										/>
									</Box>
								</Box>
								<EasTableValue
									value={row.requested_value_correction ?? 0}
									type={ETableStructureType.AMOUNT}
									size="l"
									item={row}
									tableStructureItem={tableStructure.find(
										(s) => s.selector === 'requested_value_correction',
									)}
								/>
							</Box>
							{remarks ? (
								<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
									{remarks}
								</Typography>
							) : null}
							{index < rows.length - 1 ? <Divider sx={{ my: 2 }} /> : null}
						</Box>
					);
				})}
			</Box>
		</EasCard>
	);
};
